define((require) => {
    "use strict";

    const $               = require("jquery");
    const _               = require("underscore");
    const Backbone        = require("backbone");
    const EditorEvents    = require("shared/services/EditorEvents");
    const EventDispatcher = require("shared/util/EventDispatcher");
    const KeyCode         = require("shared/services/KeyCode");
    const Icons           = require("shared/ui/assets/Icons");
    const Marionette      = require("marionette");
    const SpinnerView     = require("shared/ui/common/SpinnerView");
    const ViewScope       = require("shared/ui/common/ViewScope");

    const OVERLAY_CLASS = "zmags-lightbox-overlay";

    /**
     * Shows an element or a Backbone/Marionette view in a lightbox.
     *
     * @event open After the lightbox has been opened.
     * @event close After the lightbox has been closed.
     *
     * @author Bo Gotthardt (logic borrowed from https://github.com/buckwilson/Lightbox_me )
     * @class Lightbox
     */
    return EventDispatcher.extend({

        /**
         * @type {Window}
         */
        window: null,

        /**
         * The default position of the lightbox, when it fits within the window
         * viewport, defaults to `fixed`.
         *
         * @type {String}
         */
        defaultPosition: "fixed",

        /**
         * The default position of the lightbox, when it fits within the window
         * viewport, defaults to `fixed`.
         *
         * @type {String}
         */
        defaultTop: "50%",

        /**
         * @type {jQuery} jQuery wrapped Window instance.
         */
        $window: null,

        /**
         * @type {jQuery} jQuery wrapped Document instance.
         */
        $document: null,

        options: null,

        _element: null,

        _parentView: null,

        _spinnerElement: null,

        /**
         * @type {Backbone.View}
         */
        _view: null,

        /**
         * Disable the dynamic Element Positioning, if set to true and enables the user
         * to handle styling of the ligtbox $element with the customCSS
         *
         * @type {Boolean}
         */
        _disableElementPosition: null,

        /**
         * @private
         * @type {Number}
         * The zIndex to use for the Lightbox overlay, defaulting to a sensible value if not explicitly
         * specified as part of the options. The Lightbox contents will use this index + 1.
         */
        _zIndex: null,

        /**
         * @private @type {Boolean}
         * Guard against multiple closings, e.g. a contained View closing the Lightbox on close, while the Lightbox
         * close method will close the view.
         */
        _closed: false,

        /**
         * @constructor
         *
         * @param {jQuery|Backbone.View} element A <tt>jQuery</tt> wrapped element or a <tt>Backbone.View</tt> to show in the
         * lightbox (view will be closed/removed when the lightbox closes).
         *
         * @param {Object} options containing :
         *    @cfg {Window}  [win]                    The window to open the lightbox in. Optional, defaults to the current window.
         *    @cfg {Object}  [css]                    Optional CSS object to be applied (using jQuery) to the lightbox element.
         *    @cfg {String}  [lightboxClassName]      Optional class name to the lightbox element.
         *    @cfg {Boolean} [showSpinner]            Add a spinner on the overlay if you want visual feed back before the actual Lightbox render.
         *    @cfg {Boolean} [disableElementPosition] Disable the auto positioning if you want full control with css properties.
         *    @cfg {Number}  [zIndex]                 The z index to use for the lightbox overlay, defaulting to the
         *                                            highest z index found for any Creator Lightbox overlay in the
         *                                            dom for <tt>win</tt> + 1000.
         *
         * @param {View} parentView Optional ParentView which will provide its own view scope.
         *
         * Example usage:
         * <pre>
         *
         *  new Lightbox(element, // the view to show in the Lightbox
         *      { // options
         *          lightboxClassName: "my-lightbox",
         *          showSpinner: true,
         *          disableElementPosition: true,
         *          css: { // Pass just a width on the Lightbox or do specific styling like this.
         *              top: "40px",
         *              bottom: "40px",
         *              left: "40px",
         *              right: "40px",
         *              position: "fixed"
         *          }
         *      });
         *
         * </pre>
         */
        constructor(element, options, parentView) {
            this._element = element;

            this.options = options || {};

            this._parentView = parentView;

            this._disableElementPosition = !!options.disableElementPosition;

            // Ensure context ("this" scope) for the following methods
            _.bindAll(this, "_setOverlayHeight", "_setElementPosition", "_keypress", "_doOpen");

            this.window = options.win || window;

            // Cache the jQuery objects that are used whenever we recalculate positions.
            this.$window = $(this.window);
            this.$document = $(this.window.document);

            // If no z index is specified, find all Creator overlays, and get the max z index + 1000; we cannot
            // simply multiple by the number of overlays as the z index can be specified. This will work both
            // in the Viewer and the Editor alike...
            this._zIndex = this.options.zIndex;
            if (!this._zIndex) {
                let max = 0;
                const overlays = this.$document.find(`.${OVERLAY_CLASS}`);
                let z;
                let i;

                for (i = 0; i < overlays.length; i++) {
                    z = parseInt($(overlays[i]).css("zIndex"), 10) || 0; // TODO: easier way to do this?
                    if (z > max) {
                        max = z;
                    }
                }
                this._zIndex = max + 1000;
            }

            this.overlay = $(`<div class="${OVERLAY_CLASS}"></div>`).css({
                position:   "fixed",
                top:        0,
                bottom:     0,
                left:       0,
                right:      0,
                width:      "100%",
                zIndex:     this._zIndex,
                background: "black",
                opacity:    0.65
            });
        },

        _setupElement() {
            // Place the styling directly on the elements so we don't need to handle injecting CSS into another window.
            const customCSS = this.options.css || {};
            const lightboxClassName = this.options.lightboxClassName || "";

            // Is the passed 'element' a Backbone.View?
            if (this._element instanceof Backbone.View) {
                // Save the reference so that we can close it/clean up after us
                this._view = this._element;

                ViewScope.register(this._parentView || this, this._view);

                this._element = this._element.render.call(this._element).$el;
            }

            this.closeButton = this.createCloseButton();

            this.$element = this.createRootElement(lightboxClassName);

            this.$element
                .css(customCSS)
                .append(this.closeButton)
                .append(this._element);

            // In case we have a Backbone.View, simulate the 'show' life cycle defined by Marionette
            if (this._view) {
                Marionette.triggerMethod.call(this._view, "show");
            }
        },

        /**
         * Close button shown inside the lightbox.
         * @returns {*|String}
         */
        createCloseButton() {
            return Icons.CLOSE_IN_BOX.createElement().css({
                position: "absolute",
                top:      15,
                right:    12,
                width:    28,
                height:   28,
                cursor:   "pointer",
                zIndex:   this._zIndex
            });
        },

        /**
         * Creates the lightbox root element.
         * @param {String} lightboxClassName - optional lightbox class name
         * @returns {*|HTMLElement}
         */
        createRootElement(lightboxClassName) {
            const $zmagsLightbox = $("<div class='zmags-lightbox'></div>");

            if (lightboxClassName) {
                $zmagsLightbox.addClass(lightboxClassName);
            }

            return $zmagsLightbox;
        },

        /**
         * Open the lightbox.
         */
        open() {
            if (this.options.showSpinner) {
                // Overwrite color on the spinner, since it will be shown on the dark overlay
                this._spinnerElement = new SpinnerView({color: "#afafaf"});

                $(this.window.document.body)
                    .append(this.overlay)
                    .append(this._spinnerElement.render().$el);
            } else {
                $(this.window.document.body)
                    .append(this.overlay);
            }

            _.delay(this._doOpen, 50);

            if (this.options.editorEventProperties) {
                EditorEvents.trigger(EditorEvents.constants.OPEN_MODAL_WINDOW, this.options.editorEventProperties);
            }

            return this;
        },

        _doOpen() {
            if (this._spinnerElement) {
                this._spinnerElement.destroy();
            }

            this._setupElement();

            $(this.window.document.body)
                .append(this.$element);

            if (!this._disableElementPosition) {
                this._setElementPosition();

                this.$window
                    .on("resize", this._setElementPosition)// TODO: use our Windows.on(..) for consistency!
                    .on("scroll", this._setElementPosition);
            }

            this._setOverlayHeight();

            this.$window
                .on("resize", this._setOverlayHeight)
                .on("keyup", this._keypress);

            if (this._parentView) {
                this.listenToOnce(this._parentView, "close", this.close);
            }

            if (this._view) {
                this.listenToOnce(this._view, "close", this.close);
            }

            this.overlay.on("activate", this.close.bind(this));
            this.closeButton.on("activate", this.close.bind(this));

            this.trigger("open", this);

            this.$element.addClass("open").css({zIndex: this._zIndex + 1});
        },

        /**
         * Close the lightbox, if not already. <p>
         *
         * @param {Object} options - options object
         * @property {Boolean} silent - whether to emit an event on close
         *
         * If already closed, this is a no-op.
         */
        close(options = {}) {
            // To avoid triggering "close" events numerous times and to avoid doing dom work already done...
            if (this._closed) {
                return;
            }
            this._closed = true;

            // Handle Backbone/Marionette view close/remove. This might actually close this Lightbox recursively as a
            // close of the owning view may force a close of a used (this) Lightbox.
            if (this._view) {
                if (this._view.close) {
                    this._view.close();
                } else if (this._view.remove) {
                    this._view.remove();
                }
            }

            if (!this._disableElementPosition) {
                this.$window
                    .off("resize", this._setElementPosition)
                    .off("scroll", this._setElementPosition);
            }

            this.$window
                .off("resize", this._setOverlayHeight)
                .off("keyup", this._keypress);

            if (!options.silent) {
                // Trigger a close *before* we remove the actual element, to ensure iframes with contentWindows will still
                // be there for "close" listeners...
                this.trigger("close", this);
            }

            this.$element.remove();
            this.$element.removeClass("open");
            this.$element = null;

            this.overlay.remove();
            this.overlay = null;

            if (this.options.editorEventProperties) {
                EditorEvents.trigger(EditorEvents.constants.CLOSE_MODAL_WINDOW, this.options.editorEventProperties);
            }
        },

        _setOverlayHeight() {
            if (this.$window.height() < this.$document.height()) {
                this.overlay.css({
                    height: `${this.$document.height()}px`
                });
            } else {
                this.overlay.css({
                    height: "100%"
                });
            }
        },

        _setElementPosition() {
            // If the height of $element is bigger than the window AND the $element isn't already position absolute
            if (this.$element.height() + 80 >= this.$window.height() && this.$element.css("position") !== "fixed") {
                // we position it where the user can see it, but they can still scroll
                // so the top offset is based on the user's scroll position.
                const topOffset = $(this.window.document).scrollTop() + 40;
                this.$element.css({
                    position: "absolute",
                    top: `${topOffset}px`,
                    marginTop: 0
                });
            // If the height is less than the window height
            } else if (this.$element.height() + 80 < this.$window.height()) {
                // Set the position according to the default positioning ("fixed")
                this.$element.css({
                    position: this.defaultPosition,
                    top: this.defaultTop,
                    marginTop: (this.$element.outerHeight() / 2) * -1
                });
            }

            this.$element.css({
                left:       "50%",
                marginLeft: (this.$element.outerWidth() / 2) * -1,
                zIndex:     this._zIndex + 1
            });
        },

        _keypress(event) {
            if (event.keyCode === KeyCode.ESCAPE) {
                this.close();
            }
        }

    });
});



// WEBPACK FOOTER //
// ./src/shared/ui/common/Lightbox.js